import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaFilePdf, FaFileExcel, FaArrowLeft, FaTimes, FaSearch, FaEye, FaChevronDown, FaChevronUp, FaCalendarAlt, FaBuilding, FaUser, FaHistory, FaCheckCircle, FaRegFileAlt, FaMoneyBillWave, FaChevronLeft, FaChevronRight, FaClock } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { UserContext } from '../../Context/UserContext';

// CustomDatePicker Component (Premium Interactive Calendar with Quick Enter)
const CustomDatePicker = ({ value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('days'); // 'days' | 'months' | 'years'
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) return parsed;
    }
    return new Date();
  });
  const [yearPage, setYearPage] = useState(() => Math.floor((currentDate.getFullYear()) / 12) * 12);

  // Popup quick input states
  const [popupDay, setPopupDay] = useState(() => value ? (new Date(value).getDate() || "") : "");
  const [popupMonth, setPopupMonth] = useState(() => value ? ((new Date(value).getMonth() + 1) || "") : "");
  const [popupYear, setPopupYear] = useState(() => value ? (new Date(value).getFullYear() || "") : "");

  useEffect(() => {
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) {
        setCurrentDate(parsed);
        setYearPage(Math.floor(parsed.getFullYear() / 12) * 12);
        setPopupDay(parsed.getDate() || "");
        setPopupMonth((parsed.getMonth() + 1) || "");
        setPopupYear(parsed.getFullYear() || "");
      }
    } else {
      setPopupDay("");
      setPopupMonth("");
      setPopupYear("");
    }
  }, [value]);

  const handleQuickChange = (type, val) => {
    if (type === 'day') setPopupDay(val);
    if (type === 'month') setPopupMonth(val);
    if (type === 'year') setPopupYear(val);

    const newDay = type === 'day' ? val : popupDay;
    const newMonth = type === 'month' ? val : popupMonth;
    const newYear = type === 'year' ? val : popupYear;

    const parsedYear = parseInt(newYear, 10);
    const parsedMonth = parseInt(newMonth, 10);
    const parsedDay = parseInt(newDay, 10);

    if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12 && !isNaN(parsedDay) && parsedDay >= 1 && parsedDay <= 31) {
      const newDateObj = new Date(parsedYear, parsedMonth - 1, parsedDay);
      if (!isNaN(newDateObj)) {
        setCurrentDate(newDateObj);
        setYearPage(Math.floor(parsedYear / 12) * 12);
        const formattedMonth = String(parsedMonth).padStart(2, '0');
        const formattedDay = String(parsedDay).padStart(2, '0');
        onChange(`${parsedYear}-${formattedMonth}-${formattedDay}`);
      }
    } else if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12) {
      setCurrentDate(new Date(parsedYear, parsedMonth - 1, 1));
      setYearPage(Math.floor(parsedYear / 12) * 12);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  
  let startOffset = firstDayOfMonth.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleSelectDate = (day, e) => {
    e.stopPropagation();
    const formattedMonth = String(month + 1).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    const selectedDateStr = `${year}-${formattedMonth}-${formattedDay}`;
    onChange(selectedDateStr);
    setIsOpen(false);
    setView('days');
  };

  let selectedYear = null, selectedMonth = null, selectedDay = null;
  if (value) {
    const parsed = new Date(value);
    if (!isNaN(parsed)) {
      selectedYear = parsed.getFullYear();
      selectedMonth = parsed.getMonth();
      selectedDay = parsed.getDate();
    }
  }

  return (
    <div className="custom-datepicker-container" style={{ position: 'relative', width: '100%' }}>
      <div 
        className="custom-datepicker-input"
        onClick={() => { setIsOpen(!isOpen); setView('days'); }}
        style={{ position: 'relative' }}
      >
        <input 
          type="text" 
          readOnly
          value={value || ""} 
          placeholder={placeholder || "YYYY-MM-DD"} 
          style={{ cursor: 'pointer', background: '#ffffff', width: '100%', height: '44px', padding: '0 16px', borderRadius: '8px', border: '1px solid #E2E6EF', fontWeight: '500', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s' }}
          onFocus={(e) => { e.currentTarget.style.borderColor = '#604BE8'; e.currentTarget.style.boxShadow = '0 0 0 4px rgba(96, 75, 232, 0.15)'; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = '#E2E6EF'; e.currentTarget.style.boxShadow = 'none'; }}
        />
        <FaCalendarAlt 
          style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#604BE8', pointerEvents: 'none' }} 
        />
      </div>

      {isOpen && (
        <>
          <div className="custom-datepicker-backdrop" onClick={() => setIsOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} />
          <div className="custom-datepicker-popup" style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: '320px', background: '#ffffff', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', padding: '24px', zIndex: 1001, border: '1px solid #e2e8f0', animation: 'slideDown 0.2s ease', boxSizing: 'border-box' }}>
            
            {/* Quick Input Row inside Popup */}
            <div className="popup-quick-inputs-container" style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '0 0 24px 0', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Enter Date</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Day</span>
                  <input 
                    type="number" 
                    value={popupDay} 
                    onChange={(e) => handleQuickChange('day', e.target.value)} 
                    placeholder="DD" 
                    min="1" 
                    max="31"
                    style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} 
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Month</span>
                  <input 
                    type="number" 
                    value={popupMonth} 
                    onChange={(e) => handleQuickChange('month', e.target.value)} 
                    placeholder="MM" 
                    min="1" 
                    max="12"
                    style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} 
                  />
                </div>
                <div style={{ flex: 1.5 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Year</span>
                  <input 
                    type="number" 
                    value={popupYear} 
                    onChange={(e) => handleQuickChange('year', e.target.value)} 
                    placeholder="YYYY" 
                    min="1000" 
                    max="9999"
                    style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} 
                  />
                </div>
              </div>
            </div>

            {view === 'days' && (
              <>
                <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                  <button type="button" onClick={handlePrevMonth} className="cal-nav-btn" style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px', transition: 'all 0.2s ease' }}>
                    <FaChevronLeft />
                  </button>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span onClick={(e) => { e.stopPropagation(); setView('months'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.2s' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                      {monthNames[month]}
                    </span>
                    <span onClick={(e) => { e.stopPropagation(); setYearPage(Math.floor(year / 12) * 12); setView('years'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.2s' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                      {year}
                    </span>
                  </div>
                  <button type="button" onClick={handleNextMonth} className="cal-nav-btn" style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px', transition: 'all 0.2s ease' }}>
                    <FaChevronRight />
                  </button>
                </div>

                <div className="custom-datepicker-weekdays" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '16px' }}>
                  {["Mo", "Tu", "We", "Th", "Fri", "Sa", "Su"].map((w, i) => (
                    <span key={i} style={{ fontSize: '13px', fontWeight: '700', color: '#1B1B29' }}>{w}</span>
                  ))}
                </div>

                <div className="custom-datepicker-days" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px 4px' }}>
                  {Array.from({ length: startOffset }).map((_, i) => (
                    <div key={`empty-${i}`} />
                  ))}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const isSelected = selectedYear === year && selectedMonth === month && selectedDay === day;

                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={(e) => handleSelectDate(day, e)}
                        style={{
                          height: '36px',
                          width: '100%',
                          border: 'none',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: isSelected ? '700' : '600',
                          cursor: 'pointer',
                          background: isSelected ? '#604BE8' : 'transparent',
                          color: isSelected ? '#ffffff' : '#1B1B29',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {view === 'months' && (
              <div>
                <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setView('days'); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}>
                    <FaChevronLeft />
                  </button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29' }}>Select Month</span>
                  <div style={{ width: '36px' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {monthNames.map((mName, index) => (
                    <button
                      key={mName}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentDate(new Date(year, index, 1));
                        setView('days');
                      }}
                      style={{
                        height: '42px',
                        border: 'none',
                        borderRadius: '10px',
                        background: index === month ? '#604BE8' : '#F3F6FB',
                        color: index === month ? '#ffffff' : '#1B1B29',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {mName.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {view === 'years' && (
              <div>
                <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage - 12); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}>
                    <FaChevronLeft />
                  </button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setView('days'); }}>
                    {yearPage} - {yearPage + 11}
                  </span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage + 12); }} style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}>
                    <FaChevronRight />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {Array.from({ length: 12 }).map((_, i) => {
                    const y = yearPage + i;
                    return (
                      <button
                        key={y}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentDate(new Date(y, month, 1));
                          setView('days');
                        }}
                        style={{
                          height: '42px',
                          border: 'none',
                          borderRadius: '10px',
                          background: y === year ? '#604BE8' : '#F3F6FB',
                          color: y === year ? '#ffffff' : '#1B1B29',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {y}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Reusable CheckboxDropdown component
const CheckboxDropdown = ({ label, options, selectedValues, onToggle, placeholder, searchPlaceholder, showSearch = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const filteredOptions = options.filter(opt => {
    if (!opt) return false;
    const lbl = typeof opt === 'object' ? (opt.label || opt.name || "") : opt;
    return String(lbl).toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="premium-filter-item checkbox-dropdown-container" ref={dropdownRef} style={{ position: 'relative', minWidth: '200px', flex: 1 }}>
      <label style={{ display: 'block', color: '#777777', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>{label}</label>
      <div 
        onClick={() => setIsOpen(!isOpen)} 
        style={{ 
          height: '44px',
          padding: '0 16px',
          borderRadius: '8px',
          border: isOpen ? '1px solid #604BE8' : '1px solid #E2E6EF',
          backgroundColor: '#ffffff',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
          boxShadow: isOpen ? '0 0 0 4px rgba(96, 75, 232, 0.15)' : 'none'
        }}
      >
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, color: selectedValues.length > 0 ? '#1B1B29' : '#999999', fontWeight: '500', fontSize: '14px' }}>
          {selectedValues.length > 0 ? `${selectedValues.length} Selected` : placeholder}
        </div>
        <FaChevronDown style={{ fontSize: '12px', color: '#64748b', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </div>
      
      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', zIndex: 1000, display: 'flex', flexDirection: 'column', maxHeight: '300px' }}>
          {showSearch && (
            <div style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ position: 'relative' }}>
                <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '12px' }} />
                <input
                  type="text"
                  placeholder={searchPlaceholder || "Search..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '100%', height: '36px', padding: '0 12px 0 32px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                  autoFocus
                />
              </div>
            </div>
          )}
          <div style={{ overflowY: 'auto', padding: '8px 0' }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, idx) => {
                if (!option) return null;
                const val = (typeof option === 'object' && option !== null) ? (option.value !== undefined ? option.value : option) : option;
                const lbl = (typeof option === 'object' && option !== null) ? (option.label !== undefined ? option.label : (option.name || option)) : option;
                const isChecked = selectedValues.includes(val);
                return (
                  <div key={idx} onClick={() => onToggle(val)} style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', background: isChecked ? '#F1F4F9' : 'transparent', transition: 'background 0.2s' }} onMouseOver={(e) => { if (!isChecked) e.currentTarget.style.background = '#f8fafc' }} onMouseOut={(e) => { if (!isChecked) e.currentTarget.style.background = 'transparent' }}>
                    <input type="checkbox" checked={isChecked} readOnly style={{ width: '16px', height: '16px', accentColor: '#604BE8', cursor: 'pointer' }} />
                    <span style={{ fontSize: '14px', color: '#1B1B29', fontWeight: isChecked ? '600' : '500' }}>{lbl}</span>
                  </div>
                );
              })
            ) : <div style={{ padding: '12px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No options found</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const SessionHistory = () => {
  const navigate = useNavigate();
  const { baseUrl, getHeaders } = useContext(UserContext);
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Date filtering state using strings (YYYY-MM-DD)
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeDateTab, setActiveDateTab] = useState("All");

  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterBranches, setFilterBranches] = useState([]);
  const [filterCashiers, setFilterCashiers] = useState([]);
  
  const [branchOptions, setBranchOptions] = useState([]);
  const [cashierOptions, setCashierOptions] = useState([]);
  
  const [selectedSession, setSelectedSession] = useState(null);
  
  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const headers = getHeaders();
        const response = await axios.get(`${baseUrl}/api/session-history`, { headers });
        if (response.data && response.data.status === 'success') {
          const rawData = response.data.message || [];
          
          const formattedSessions = rawData.map(entry => {
            const isClosed = entry.status === 'Closed';
            
            const formatDate = (d) => d ? d.toISOString().split('T')[0] : '-';
            const formatTime = (d) => d ? d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-';
            const openingDateObj = new Date(entry.creation);
            const closingDateObj = entry.period_end_date ? new Date(entry.period_end_date) : null;
            
            const pr = entry.payment_reconciliation || [];
            let cashSales = 0, cardSales = 0, upiSales = 0, walletSales = 0;
            let expectedCash = entry.opening_amount || 0;
            let actualCash = isClosed ? entry.opening_amount : 0;
            
            pr.forEach(p => {
                const mode = (p.mode_of_payment || '').toLowerCase();
                const salesAmt = parseFloat(p.sales_amount) || 0;
                if (mode === 'cash') {
                    cashSales += salesAmt;
                    expectedCash = parseFloat(p.expected_amount) || 0;
                    if (isClosed) actualCash = parseFloat(p.closing_amount) || 0;
                } else if (mode === 'card') {
                    cardSales += salesAmt;
                } else if (mode === 'upi') {
                    upiSales += salesAmt;
                } else if (mode === 'wallet') {
                    walletSales += salesAmt;
                }
            });
            
            const difference = isClosed ? (actualCash - expectedCash) : 0;

            return {
              id: entry.name,
              company: entry.company || 'N/A',
              branch: entry.branch_name || entry.branch || 'N/A',
              cashier: entry.user || entry.pos_profile || 'N/A',
              status: entry.status || 'Active',
              openingDate: formatDate(openingDateObj),
              openingTime: formatTime(openingDateObj),
              closingDate: isClosed ? formatDate(closingDateObj) : '-',
              closingTime: isClosed ? formatTime(closingDateObj) : '-',
              openingBalance: entry.opening_amount || 0,
              totalSales: entry.net_total || 0,
              cashSales: cashSales,
              cardSales: cardSales,
              upiSales: upiSales,
              walletSales: walletSales,
              expectedCash: expectedCash,
              actualCash: actualCash,
              discrepancy: difference,
              notes: entry.notes || '-'
            };
          });
          
          setSessions(formattedSessions);
          
          const cashiers = [...new Set(formattedSessions.map(s => s.cashier).filter(c => c !== 'N/A'))].sort();
          setCashierOptions(cashiers);
          
          try {
            const branchResponse = await axios.get(`${baseUrl}/api/branches`, { headers });
            if (branchResponse.data && Array.isArray(branchResponse.data)) {
              const allBranches = branchResponse.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name));
              setBranchOptions([...new Set(allBranches)].sort());
            } else {
              const branches = [...new Set(formattedSessions.map(s => s.branch).filter(b => b !== 'N/A'))].sort();
              setBranchOptions(branches);
            }
          } catch (branchErr) {
            console.error("Error fetching branches:", branchErr);
            const branches = [...new Set(formattedSessions.map(s => s.branch).filter(b => b !== 'N/A'))].sort();
            setBranchOptions(branches);
          }
        }
      } catch (err) {
        console.error("Error fetching session history:", err);
      } finally {
        setLoading(false);
      }
    };
    
    if (baseUrl !== undefined) {
       fetchSessions();
    }
  }, [baseUrl]);

  // Date Tab Logic
  const handleDateTabClick = (tab) => {
    setActiveDateTab(tab);
    const now = new Date();
    
    const formatStr = (d) => {
      const yr = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      return `${yr}-${mo}-${da}`;
    };

    if (tab === 'All') {
      setFromDate("");
      setToDate("");
    } else if (tab === 'Today') {
      const todayStr = formatStr(now);
      setFromDate(todayStr);
      setToDate(todayStr);
    } else if (tab === 'Week') {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
      const startOfWeek = new Date(now.setDate(diff));
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      setFromDate(formatStr(startOfWeek));
      setToDate(formatStr(endOfWeek));
    } else if (tab === 'Month') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setFromDate(formatStr(startOfMonth));
      setToDate(formatStr(endOfMonth));
    } else if (tab === 'Year') {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const endOfYear = new Date(now.getFullYear(), 11, 31);
      setFromDate(formatStr(startOfYear));
      setToDate(formatStr(endOfYear));
    }
  };

  const handleCustomDateChange = (isFrom, val) => {
    setActiveDateTab('Custom');
    if (isFrom) setFromDate(val);
    else setToDate(val);
  };

  const filteredSessions = useMemo(() => {
    return sessions.filter(session => {
      const matchesSearch = 
        session.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.cashier.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.branch.toLowerCase().includes(searchTerm.toLowerCase());
        
      const matchesStatus = filterStatuses.length === 0 || filterStatuses.includes(session.status);
      const matchesBranch = filterBranches.length === 0 || filterBranches.includes(session.branch);
      const matchesCashier = filterCashiers.length === 0 || filterCashiers.includes(session.cashier);
      
      let matchesDate = true;
      if (fromDate || toDate) {
        const sessionDate = new Date(session.openingDate);
        if (fromDate) {
          const fd = new Date(fromDate);
          fd.setHours(0,0,0,0);
          if (sessionDate < fd) matchesDate = false;
        }
        if (toDate) {
          const td = new Date(toDate);
          td.setHours(23, 59, 59, 999);
          if (sessionDate > td) matchesDate = false;
        }
      }
      
      return matchesSearch && matchesStatus && matchesBranch && matchesCashier && matchesDate;
    });
  }, [sessions, searchTerm, filterStatuses, filterBranches, filterCashiers, fromDate, toDate]);

  const stats = useMemo(() => {
    const total = filteredSessions.length;
    const active = filteredSessions.filter(s => s.status === 'Active' || s.status === 'Open').length;
    const closed = filteredSessions.filter(s => s.status === 'Closed').length;
    const totalSales = filteredSessions.reduce((acc, curr) => acc + curr.totalSales, 0);
    
    return { total, active, closed, totalSales };
  }, [filteredSessions]);

  const toggleFilter = (setter, value) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const handleExportCSV = () => {
    if (filteredSessions.length === 0) return alert("No data to export.");
    const ws = XLSX.utils.json_to_sheet(filteredSessions);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sessions");
    XLSX.writeFile(wb, `Session_History_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportPDF = () => {
    if (filteredSessions.length === 0) return alert("No data to export.");
    
    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Session History PDF</title>
          <style>
            @media print { body { margin: 0; } @page { margin: 10mm; size: A4 landscape; } }
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; color: #1B1B29; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; }
            th { background-color: #f1f5f9; font-weight: bold; }
            .stats { display: flex; justify-content: space-between; margin-bottom: 20px; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Session History Report</h1>
          <div class="stats">
            <span>Total Sessions: ${stats.total}</span>
            <span>Active: ${stats.active}</span>
            <span>Closed: ${stats.closed}</span>
            <span>Total Sales: ₹${stats.totalSales.toFixed(2)}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Session ID</th>
                <th>Branch</th>
                <th>Cashier</th>
                <th>Opened At</th>
                <th>Closed At</th>
                <th>Status</th>
                <th>Total Sales (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${filteredSessions.map(s => `
                <tr>
                  <td>${s.id}</td>
                  <td>${s.branch}</td>
                  <td>${s.cashier}</td>
                  <td>${s.openingDate} ${s.openingTime}</td>
                  <td>${s.closingDate} ${s.closingTime}</td>
                  <td>${s.status}</td>
                  <td>${s.totalSales.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <script>
            window.onload = () => { window.print(); };
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  return (
    <div style={{ padding: '0', backgroundColor: '#F8F9FD', minHeight: '100vh', fontFamily: "'Inter', sans-serif", marginTop: '0', display: 'flex', flexDirection: 'column' }}>
      <style>
        {`
          .premium-table { width: 100%; border-collapse: separate; border-spacing: 0; }
          .premium-table th { background: #F8F9FD; padding: 18px 24px; text-align: left; font-size: 13px; font-weight: 700; color: #4A4A5A; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #E2E6EF; position: sticky; top: 0; z-index: 10; white-space: nowrap; }
          .premium-table td { padding: 20px 24px; border-bottom: 1px solid #F1F3F8; font-size: 14px; vertical-align: middle; background: #ffffff; transition: background 0.2s; }
          .premium-table tbody tr { transition: all 0.2s ease; cursor: pointer; }
          .premium-table tbody tr:hover td { background: #F8F9FD; }
          .premium-table tbody tr:last-child td { border-bottom: none; }
          
          .action-btn-view { display: flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 10px; border: none; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.2s ease; background: #F1F4F9; color: #604BE8; }
          .action-btn-view:hover { background: #604BE8 !important; color: #ffffff !important; box-shadow: 0 4px 15px rgba(96, 75, 232, 0.3); transform: translateY(-2px); }

          ::-webkit-scrollbar { width: 6px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb { background: #DCE3EE; border-radius: 10px; }
          ::-webkit-scrollbar-thumb:hover { background: #A0A5B5; }

          @keyframes fadeInScale {
            from { opacity: 0; transform: scale(0.95) translateY(10px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          
          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          .premium-card { animation: fadeInScale 0.4s cubic-bezier(0, 0, 0.2, 1); }
          
          .date-tab-btn {
            padding: 8px 16px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            border: none;
            transition: all 0.2s ease;
          }
        `}
      </style>

      {/* Top Bar Section - Grouped Back Button and Title without large gap */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', padding: '24px 48px', width: '100%', boxSizing: 'border-box', flex: '0 0 auto' }}>
        
        {/* Left Side: Back Button & Page Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <button 
            onClick={() => navigate(-1)}
            style={{ height: '44px', padding: '0 20px', borderRadius: '10px', border: '1px solid #E2E6EF', background: '#ffffff', color: '#604BE8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)' }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = '#604BE8'; e.currentTarget.style.background = '#eff2f9'; }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = '#E2E6EF'; e.currentTarget.style.background = '#ffffff'; }}
          >
            <FaArrowLeft style={{ fontSize: '12px' }} /> Back
          </button>
          
          <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#1B1B29', letterSpacing: '-0.5px' }}>Session History</h1>
        </div>

        {/* Right Side: Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={handleExportCSV}
            style={{ height: '44px', padding: '0 16px', borderRadius: '8px', backgroundColor: '#ffffff', border: '1px solid #16a34a', color: '#16a34a', fontWeight: '600', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#f0fdf4'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; }}
          >
            <FaFileExcel /> Excel
          </button>
          <button
            onClick={handleExportPDF}
            style={{ height: '44px', padding: '0 16px', borderRadius: '8px', backgroundColor: '#ffffff', border: '1px solid #ef4444', color: '#ef4444', fontWeight: '600', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; }}
          >
            <FaFilePdf /> PDF
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{ padding: "0 48px", marginBottom: '24px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', flex: '0 0 auto' }}>
        <div className="premium-card" style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', border: '1px solid #E2E6EF', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: '#e0f2fe', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
            <FaHistory />
          </div>
          <div>
            <p style={{ margin: '0 0 4px', color: '#64748b', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Sessions</p>
            <h3 style={{ margin: 0, color: '#1B1B29', fontSize: '28px', fontWeight: '800' }}>{stats.total}</h3>
          </div>
        </div>
        <div className="premium-card" style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', border: '1px solid #E2E6EF', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
            <FaCheckCircle />
          </div>
          <div>
            <p style={{ margin: '0 0 4px', color: '#64748b', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Sessions</p>
            <h3 style={{ margin: 0, color: '#1B1B29', fontSize: '28px', fontWeight: '800' }}>{stats.active}</h3>
          </div>
        </div>
        <div className="premium-card" style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', border: '1px solid #E2E6EF', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: '#f1f5f9', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
            <FaRegFileAlt />
          </div>
          <div>
            <p style={{ margin: '0 0 4px', color: '#64748b', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Closed Sessions</p>
            <h3 style={{ margin: 0, color: '#1B1B29', fontSize: '28px', fontWeight: '800' }}>{stats.closed}</h3>
          </div>
        </div>
        <div className="premium-card" style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', border: '1px solid #E2E6EF', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
            <FaMoneyBillWave />
          </div>
          <div>
            <p style={{ margin: '0 0 4px', color: '#64748b', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Volume</p>
            <h3 style={{ margin: 0, color: '#1B1B29', fontSize: '28px', fontWeight: '800' }}>₹{stats.totalSales.toFixed(2)}</h3>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={{ padding: "0 48px 24px 48px", flex: '0 0 auto' }}>
        <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '20px', background: '#ffffff', padding: '24px', borderRadius: '16px', border: '1px solid #E2E6EF', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
          
          {/* Quick Date Filters Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '8px', background: '#f8fafc', padding: '4px', borderRadius: '10px' }}>
              {['All', 'Today', 'Week', 'Month', 'Year'].map(tab => (
                <button
                  key={tab}
                  className="date-tab-btn"
                  onClick={() => handleDateTabClick(tab)}
                  style={{
                    background: activeDateTab === tab ? '#604BE8' : 'transparent',
                    color: activeDateTab === tab ? '#ffffff' : '#64748b',
                  }}
                >
                  {tab === 'Week' ? 'This Week' : tab === 'Month' ? 'This Month' : tab === 'Year' ? 'This Year' : tab}
                </button>
              ))}
            </div>
            
            {(searchTerm || fromDate || toDate || filterStatuses.length > 0 || filterBranches.length > 0 || filterCashiers.length > 0) && (
              <button 
                onClick={() => {
                  setSearchTerm("");
                  setActiveDateTab('All');
                  setFromDate("");
                  setToDate("");
                  setFilterStatuses([]);
                  setFilterBranches([]);
                  setFilterCashiers([]);
                }}
                style={{ height: '36px', padding: '0 16px', borderRadius: '8px', border: '1px solid #FF595E', background: '#fff0f0', color: '#FF595E', fontWeight: '600', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease' }}
                onMouseOver={(e) => { e.currentTarget.style.background = '#FF595E'; e.currentTarget.style.color = '#ffffff'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = '#fff0f0'; e.currentTarget.style.color = '#FF595E'; }}
              >
                <FaTimes /> Clear Filters
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
            {/* Search Box */}
            <div style={{ flex: 1, minWidth: '250px' }}>
              <label style={{ display: 'block', color: '#777777', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Search Sessions</label>
              <div style={{ position: 'relative' }}>
                <FaSearch style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Session ID, Branch, Cashier..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '100%', height: '44px', padding: '0 16px 0 42px', borderRadius: '8px', border: '1px solid #E2E6EF', fontSize: '14px', outline: 'none', transition: 'all 0.2s', fontWeight: '500', boxSizing: 'border-box' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#604BE8'; e.currentTarget.style.boxShadow = '0 0 0 4px rgba(96, 75, 232, 0.15)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#E2E6EF'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {/* Custom Date Range Pickers */}
              <div style={{ minWidth: '160px' }}>
                <label style={{ display: 'block', color: '#777777', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>From Date</label>
                <CustomDatePicker
                  value={fromDate}
                  onChange={(val) => handleCustomDateChange(true, val)}
                  placeholder="Start Date"
                />
              </div>
              <div style={{ minWidth: '160px' }}>
                <label style={{ display: 'block', color: '#777777', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>To Date</label>
                <CustomDatePicker
                  value={toDate}
                  onChange={(val) => handleCustomDateChange(false, val)}
                  placeholder="End Date"
                />
              </div>

              {/* Dropdowns */}
              <CheckboxDropdown
                label="Status"
                options={['Open', 'Closed', 'Active']}
                selectedValues={filterStatuses}
                onToggle={(val) => toggleFilter(setFilterStatuses, val)}
                placeholder="All Statuses"
              />
              <CheckboxDropdown
                label="Branch"
                options={branchOptions}
                selectedValues={filterBranches}
                onToggle={(val) => toggleFilter(setFilterBranches, val)}
                placeholder="All Branches"
                showSearch={true}
              />
              <CheckboxDropdown
                label="Cashier / Profile"
                options={cashierOptions}
                selectedValues={filterCashiers}
                onToggle={(val) => toggleFilter(setFilterCashiers, val)}
                placeholder="All Cashiers"
                showSearch={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div style={{ flex: 1, padding: "0 48px 48px", overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '80px 40px', textAlign: 'center', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)', border: '1px solid #E2E6EF', color: '#777777', flex: 1 }} className="premium-card">
            <div style={{ fontSize: '42px', marginBottom: '16px', animation: 'spin 2s linear infinite' }}>⏳</div>
            <p style={{ fontWeight: '700', fontSize: '18px', color: '#1B1B29' }}>Loading Sessions...</p>
            <p style={{ fontSize: '14px' }}>Fetching latest session records from the server.</p>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '80px 40px', textAlign: 'center', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)', border: '1px solid #E2E6EF', flex: 1 }} className="premium-card">
            <div style={{ fontSize: '54px', marginBottom: '20px', color: '#cbd5e1' }}><FaHistory /></div>
            <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#1B1B29', margin: '0 0 10px', letterSpacing: '-0.5px' }}>No Sessions Found</h3>
            <p style={{ color: '#777777', marginBottom: '30px', maxWidth: '400px', margin: '0 auto 30px', lineHeight: '1.6', fontSize: '14px' }}>No records match your current filters. Try adjusting your search criteria.</p>
          </div>
        ) : (
          <div style={{ maxHeight: '100%', overflowY: 'auto', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)', backgroundColor: '#ffffff', border: '1px solid #E2E6EF' }} className="premium-card">
            <table className="premium-table">
              <thead>
                <tr>
                  <th>Session ID</th>
                  <th>Date & Time</th>
                  <th style={{ textAlign: 'right' }}>Opening</th>
                  <th style={{ textAlign: 'right' }}>Sales</th>
                  <th style={{ textAlign: 'right' }}>Expected Closing</th>
                  <th style={{ textAlign: 'right' }}>Actual Closing</th>
                  <th style={{ textAlign: 'right' }}>Difference</th>
                  <th>User / Profile</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right', paddingRight: '24px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((session) => (
                  <tr key={session.id} onClick={() => setSelectedSession(session)}>
                    <td style={{ fontWeight: '700', color: '#1B1B29', fontSize: '14px' }}>{session.id}</td>
                    
                    {/* Added Date and Time Column */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FaCalendarAlt style={{ color: '#94a3b8', fontSize: '11px' }} />
                          <span style={{ fontWeight: '600', color: '#4A4A5A', fontSize: '13px' }}>{session.openingDate}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FaClock style={{ color: '#94a3b8', fontSize: '11px' }} />
                          <span style={{ color: '#64748b', fontSize: '12px', fontWeight: '500' }}>{session.openingTime}</span>
                        </div>
                      </div>
                    </td>

                    <td style={{ textAlign: 'right', fontWeight: '500' }}>₹{session.openingBalance.toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontWeight: '700', color: '#604BE8' }}>₹{session.totalSales.toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontWeight: '500' }}>₹{session.expectedCash.toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontWeight: '700', color: '#1B1B29' }}>₹{session.actualCash.toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontWeight: '700', color: session.discrepancy < 0 ? '#ef4444' : (session.discrepancy > 0 ? '#f59e0b' : '#10b981') }}>
                      ₹{session.discrepancy.toFixed(2)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <FaUser style={{ color: '#94a3b8', fontSize: '12px' }} />
                        <span style={{ fontWeight: '600', color: '#1B1B29', fontSize: '14px' }}>{session.cashier}</span>
                      </div>
                      {session.branch && session.branch !== 'N/A' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                          <FaBuilding style={{ color: '#94a3b8', fontSize: '12px' }} />
                          <span style={{ color: '#64748b' }}>{session.branch}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{ 
                        padding: '6px 12px', 
                        borderRadius: '20px', 
                        fontSize: '12px', 
                        fontWeight: '700', 
                        textTransform: 'uppercase',
                        backgroundColor: session.status === 'Open' || session.status === 'Active' ? '#dcfce7' : '#f1f5f9',
                        color: session.status === 'Open' || session.status === 'Active' ? '#16a34a' : '#475569'
                      }}>
                        {session.status}
                      </span>
                    </td>
                    <td style={{ paddingRight: '24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {/* Larger View Button */}
                        <button 
                          className="action-btn-view"
                          onClick={(e) => { e.stopPropagation(); setSelectedSession(session); }}
                          title="View Details"
                        >
                          <FaEye style={{ fontSize: '16px' }} /> View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {selectedSession && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(27, 27, 41, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000, backdropFilter: 'blur(8px)' }} onClick={() => setSelectedSession(null)}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '20px', width: '800px', maxWidth: '95%', padding: '0', boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)', maxHeight: '90vh', overflow: 'hidden', position: 'relative', animation: 'fadeInScale 0.3s ease-out', display: 'flex', flexDirection: 'column', color: '#1B1B29' }} onClick={(e) => e.stopPropagation()}>
            
            <div style={{ background: '#604BE8', padding: '36px 36px 24px', position: 'relative', color: '#ffffff' }}>
              <button onClick={() => setSelectedSession(null)} style={{ position: 'absolute', top: '24px', right: '24px', border: 'none', background: 'rgba(255,255,255,0.2)', color: '#ffffff', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'} onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}>
                <FaTimes />
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '64px', height: '64px', background: '#ffffff', color: '#604BE8', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '28px', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}>
                  <FaHistory />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800' }}>Session Details</h2>
                  <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '15px', fontWeight: '500' }}>{selectedSession.id}</p>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <span style={{ padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', backgroundColor: 'rgba(255,255,255,0.2)', color: '#ffffff', backdropFilter: 'blur(4px)' }}>
                    {selectedSession.status}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '32px 36px', background: '#f8fafc' }}>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700', color: '#1B1B29', display: 'flex', alignItems: 'center', gap: '8px' }}><FaUser style={{ color: '#604BE8' }}/> Basic Info</h3>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div><span style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>Cashier / Profile</span><span style={{ fontWeight: '600', color: '#1B1B29' }}>{selectedSession.cashier}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>Branch</span><span style={{ fontWeight: '600', color: '#1B1B29' }}>{selectedSession.branch}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>Company</span><span style={{ fontWeight: '600', color: '#1B1B29' }}>{selectedSession.company}</span></div>
                  </div>
                </div>

                <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700', color: '#1B1B29', display: 'flex', alignItems: 'center', gap: '8px' }}><FaCalendarAlt style={{ color: '#604BE8' }}/> Timeline</h3>
                  <div style={{ display: 'grid', gap: '16px' }}>
                    <div>
                      <span style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Opened At</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ background: '#eff2f9', color: '#604BE8', padding: '6px 12px', borderRadius: '6px', fontWeight: '600', fontSize: '14px' }}>{selectedSession.openingDate}</span>
                        <span style={{ fontWeight: '600', color: '#1B1B29' }}>{selectedSession.openingTime}</span>
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Closed At</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ background: '#eff2f9', color: '#604BE8', padding: '6px 12px', borderRadius: '6px', fontWeight: '600', fontSize: '14px' }}>{selectedSession.closingDate}</span>
                        <span style={{ fontWeight: '600', color: '#1B1B29' }}>{selectedSession.closingTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ background: '#ffffff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: '15px', fontWeight: '700', color: '#1B1B29', display: 'flex', alignItems: 'center', gap: '8px' }}><FaMoneyBillWave style={{ color: '#604BE8' }}/> Financial Summary</h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #f1f5f9', textAlign: 'center' }}>
                    <span style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Opening Balance</span>
                    <span style={{ fontWeight: '700', color: '#1B1B29', fontSize: '20px' }}>₹{selectedSession.openingBalance.toFixed(2)}</span>
                  </div>
                  <div style={{ background: '#eff2f9', padding: '16px', borderRadius: '10px', border: '1px solid #e0e7ff', textAlign: 'center' }}>
                    <span style={{ color: '#604BE8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Total Sales</span>
                    <span style={{ fontWeight: '800', color: '#604BE8', fontSize: '20px' }}>₹{selectedSession.totalSales.toFixed(2)}</span>
                  </div>
                  <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #f1f5f9', textAlign: 'center' }}>
                    <span style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Expected Closing</span>
                    <span style={{ fontWeight: '700', color: '#1B1B29', fontSize: '20px' }}>₹{selectedSession.expectedCash.toFixed(2)}</span>
                  </div>
                </div>

                <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '16px 24px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ color: '#64748b', fontWeight: '500' }}>Cash Sales</span>
                    <span style={{ fontWeight: '600', color: '#1B1B29' }}>₹{selectedSession.cashSales.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ color: '#64748b', fontWeight: '500' }}>Card Sales</span>
                    <span style={{ fontWeight: '600', color: '#1B1B29' }}>₹{selectedSession.cardSales.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ color: '#64748b', fontWeight: '500' }}>UPI Sales</span>
                    <span style={{ fontWeight: '600', color: '#1B1B29' }}>₹{selectedSession.upiSales.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ color: '#64748b', fontWeight: '500' }}>Wallet Sales</span>
                    <span style={{ fontWeight: '600', color: '#1B1B29' }}>₹{selectedSession.walletSales.toFixed(2)}</span>
                  </div>

                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '2px dashed #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#1B1B29', fontWeight: '700', fontSize: '15px' }}>Actual Closing Cash</span>
                    <span style={{ fontWeight: '800', color: '#1B1B29', fontSize: '18px' }}>₹{selectedSession.actualCash.toFixed(2)}</span>
                  </div>
                  <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: selectedSession.discrepancy < 0 ? '#fef2f2' : (selectedSession.discrepancy > 0 ? '#fffbeb' : '#f0fdf4'), padding: '12px 16px', borderRadius: '8px' }}>
                    <span style={{ color: '#1B1B29', fontWeight: '700', fontSize: '15px' }}>Difference</span>
                    <span style={{ fontWeight: '800', fontSize: '18px', color: selectedSession.discrepancy < 0 ? '#ef4444' : (selectedSession.discrepancy > 0 ? '#d97706' : '#16a34a') }}>
                      ₹{selectedSession.discrepancy.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {selectedSession.notes && selectedSession.notes !== "-" && (
                <div style={{ background: '#fffbeb', padding: '20px', borderRadius: '12px', border: '1px solid #fde68a', marginTop: '24px' }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notes</h3>
                  <p style={{ margin: 0, color: '#92400e', fontSize: '15px', lineHeight: '1.5' }}>{selectedSession.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionHistory;
